// Single-instance lock (IMPLEMENTATION §2.9 "Save", §3 M11): one tab / home-screen window owns the save.
// Transport: BroadcastChannel('gunmerger'); fallback when it is missing: the localStorage `storage` event
// on the key `gunmerger.lock` (other same-origin documents only, like a channel); neither → no lock (the
// instance simply owns the save).
//
// Protocol — the NEWEST instance wins, with a save handover:
//   claim  {k:'claim', id, at}   sent by an instance that wants the save (at boot, before it loads, and
//                                when a page comes back from the bfcache). `at` = wall ms of the claim;
//                                newer = larger (at, id), compared lexicographically.
//   yield  {k:'yield', id, to, main?}  sent by the current OWNER when a newer claim arrives, AFTER its
//                                onLost() hook flushed its last save — so the claimer loads the freshest
//                                state. `main` = a fingerprint of the save it left under the main key
//                                (`handoverToken`); the claimer resolves only once ITS storage shows that
//                                save (`awaitMain`, bounded): Chromium delivers another tab's localStorage
//                                writes asynchronously and unordered with channel messages, so a claimer
//                                that touched localStorage before the yield (the marks below) could
//                                otherwise read the previous main.
// A claimer waits for a `yield` addressed to it or `handoverMs` (ui.json save.lockHandoverMs; nobody else
// is running, or the owner is frozen), then owns the save. An owner or claimer that receives an OLDER claim
// answers with its own claim again, so a late claimer loses instead of timing out into a second owner.
// A lost instance never claims again by itself and ignores every message (the ElsewhereOverlay's "Play
// here" reloads the page, which claims as the newest). Messages carrying our own id are ignored.
//
// Storage marks (M11 review fix; `LockMarks`, localStorage in the app) make the lock independent of message
// delivery — a frozen / suspended owner may miss a claim for good (Chromium drops BroadcastChannel messages
// to a paused page) or hear it only after the new owner has played:
//   gunmerger.lock.at     the last claim stamp: a claim uses max(wall clock, last stamp + 1), so a clock set
//                         back never makes a new tab older than the owner (newest still wins)
//   gunmerger.lock.owner  the id of the instance that became owner last; `holdsMark()` is false for a stale
//                         owner, which then must not write (game/lifecycle.ts checks it before every save).

export type LockMsg =
  | { k: 'claim'; id: string; at: number }
  | { k: 'yield'; id: string; to: string; main?: string };

export type LockState = 'idle' | 'claiming' | 'owner' | 'lost';

export interface LockTransport {
  readonly kind: 'broadcast' | 'storage' | 'none';
  post(m: LockMsg): void;
  listen(cb: (m: LockMsg) => void): void;
  close(): void;
}

export interface Timers {
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(h: unknown): void;
}

/** Storage-level marks of the lock (see the header); absent → messages only. */
export interface LockMarks {
  /** A claim stamp newer than every earlier claim here: max(wallMs, last stamp + 1), stored. */
  stamp(wallMs: number): number;
  /** The instance that became owner last (null: none recorded, or storage unreadable). */
  owner(): string | null;
  setOwner(id: string): void;
}

export interface InstanceLockOptions {
  id: string;
  transport: LockTransport;
  /** Wall clock (Date.now in the app). */
  now(): number;
  /** Storage marks (the app: `storageMarks(window)`); tests may omit them. */
  marks?: LockMarks;
  timers: Timers;
  /** ui.json save.lockHandoverMs. */
  handoverMs: number;
  /**
   * A newer instance took the save. `wasOwner`: this instance had loaded / written the save (flush it now,
   * synchronously — the `yield` is posted right after). Called once.
   */
  onLost(wasOwner: boolean): void;
  /** A losing OWNER, right after onLost: the fingerprint of the save it leaves (the yield's `main`). */
  handoverToken?(): string | null;
  /**
   * The claimer got a yield with `main`: resolves once this instance's storage shows that save (or after
   * its own bound). Absent → owner at once.
   */
  awaitMain?(token: string): Promise<unknown>;
}

export const LOCK_CHANNEL = 'gunmerger';
export const LOCK_KEY = 'gunmerger.lock';
export const LOCK_AT_KEY = 'gunmerger.lock.at';
export const LOCK_OWNER_KEY = 'gunmerger.lock.owner';

function isMsg(v: unknown): v is LockMsg {
  if (typeof v !== 'object' || v === null) return false;
  const m = v as Record<string, unknown>;
  if (typeof m.id !== 'string') return false;
  if (m.k === 'claim') return typeof m.at === 'number' && Number.isFinite(m.at);
  if (m.k === 'yield')
    return typeof m.to === 'string' && (m.main === undefined || typeof m.main === 'string');
  return false;
}

/** (at, id) of `a` is newer than (at, id) of `b`. */
export function newer(a: { at: number; id: string }, b: { at: number; id: string }): boolean {
  return a.at > b.at || (a.at === b.at && a.id > b.id);
}

export class InstanceLock {
  private readonly o: InstanceLockOptions;
  private st: LockState = 'idle';
  private at = 0;
  private waiting: ((s: LockState) => void) | null = null;
  private timer: unknown = null;
  /** Claims made so far: a storage wait (awaitMain) of an earlier claim must not settle a newer one. */
  private gen = 0;

  constructor(o: InstanceLockOptions) {
    this.o = o;
    o.transport.listen((m) => this.onMessage(m));
  }

  get state(): LockState {
    return this.st;
  }

  get id(): string {
    return this.o.id;
  }

  get transportKind(): LockTransport['kind'] {
    return this.o.transport.kind;
  }

  /**
   * Claims the save: announces this instance as the newest and resolves once the previous owner yielded
   * (or `handoverMs` passed) with 'owner', or with 'lost' when an even newer instance claimed meanwhile.
   * A lost instance stays lost.
   */
  claim(): Promise<LockState> {
    if (this.st === 'lost') return Promise.resolve('lost');
    this.settle(); // a claim still waiting (bfcache re-claim) ends here
    this.gen += 1;
    this.st = 'claiming';
    const wall = this.o.now();
    this.at = this.o.marks ? this.o.marks.stamp(wall) : wall;
    this.post({ k: 'claim', id: this.o.id, at: this.at });
    if (this.o.transport.kind === 'none') {
      this.own();
      return Promise.resolve('owner');
    }
    return new Promise<LockState>((resolve) => {
      this.waiting = resolve;
      this.timer = this.o.timers.setTimeout(() => {
        this.timer = null;
        if (this.st === 'claiming') this.own();
        this.settle();
      }, this.o.handoverMs);
    });
  }

  /**
   * False when the storage mark names ANOTHER instance: a newer one became owner although this one never
   * heard its claim (or heard it late). Such a stale owner must not write. True without marks / storage.
   */
  holdsMark(): boolean {
    const m = this.o.marks?.owner() ?? null;
    return m === null || m === this.o.id;
  }

  /** Gives the save up without a message (a storage check found a newer owner). onLost is not called. */
  abandon(): void {
    if (this.st === 'lost') return;
    this.st = 'lost';
    this.settle();
  }

  /** Stops listening (page unload / tests). A pending claim resolves with the current state. */
  close(): void {
    this.settle();
    this.o.transport.close();
  }

  private own(): void {
    this.st = 'owner';
    try {
      this.o.marks?.setOwner(this.o.id);
    } catch {
      // blocked storage: messages only
    }
  }

  private clearTimer(): void {
    if (this.timer !== null) {
      this.o.timers.clearTimeout(this.timer);
      this.timer = null;
    }
  }

  private settle(): void {
    this.clearTimer();
    const w = this.waiting;
    this.waiting = null;
    w?.(this.st);
  }

  private post(m: LockMsg): void {
    try {
      this.o.transport.post(m);
    } catch {
      // a closed channel or blocked storage: nothing to tell
    }
  }

  private onMessage(m: LockMsg): void {
    if (m.id === this.o.id || this.st === 'lost' || this.st === 'idle') return;
    if (m.k === 'yield') {
      if (m.to === this.o.id && this.st === 'claiming') this.accept(m.main);
      return;
    }
    if (newer(m, { at: this.at, id: this.o.id })) {
      const wasOwner = this.st === 'owner';
      this.st = 'lost';
      let main: string | null = null;
      try {
        this.o.onLost(wasOwner);
        if (wasOwner) main = this.o.handoverToken?.() ?? null;
      } finally {
        this.post(
          main === null
            ? { k: 'yield', id: this.o.id, to: m.id }
            : { k: 'yield', id: this.o.id, to: m.id, main },
        );
        this.settle();
      }
      return;
    }
    // an older claim (a late claimer): tell it we are newer
    this.post({ k: 'claim', id: this.o.id, at: this.at });
  }

  /** The owner yielded to us: own now, or once our storage shows the save it left (still 'claiming'). */
  private accept(main: string | undefined): void {
    const wait = main !== undefined ? this.o.awaitMain?.(main) : undefined;
    if (wait === undefined) {
      this.own();
      this.settle();
      return;
    }
    this.clearTimer(); // the yield came: the handover timeout no longer applies
    const gen = this.gen;
    const done = (): void => {
      if (gen !== this.gen) return; // a newer claim of ours took over
      if (this.st === 'claiming') this.own(); // (lost meanwhile: an even newer instance claimed)
      this.settle();
    };
    void wait.then(done, done);
  }
}

// ---- transports -----------------------------------------------------------------------------------------

interface ChannelLike {
  postMessage(m: unknown): void;
  close(): void;
  onmessage: ((ev: MessageEvent) => void) | null;
}

type ChannelCtor = new (name: string) => ChannelLike;

export function broadcastTransport(Ctor: ChannelCtor, name = LOCK_CHANNEL): LockTransport {
  const ch = new Ctor(name);
  return {
    kind: 'broadcast',
    post: (m) => ch.postMessage(m),
    listen(cb) {
      ch.onmessage = (ev) => {
        if (isMsg(ev.data)) cb(ev.data);
      };
    },
    close() {
      ch.onmessage = null;
      ch.close();
    },
  };
}

/** Fallback without BroadcastChannel: a localStorage key whose `storage` events reach the other documents. */
export function storageTransport(win: Window, key = LOCK_KEY): LockTransport {
  let seq = 0;
  let handler: ((ev: StorageEvent) => void) | null = null;
  return {
    kind: 'storage',
    post(m) {
      seq += 1;
      // `n` makes every write a change (an identical value fires no storage event)
      win.localStorage.setItem(key, JSON.stringify({ ...m, n: seq }));
    },
    listen(cb) {
      handler = (ev) => {
        if (ev.key !== key || ev.newValue === null) return;
        try {
          const v: unknown = JSON.parse(ev.newValue);
          if (isMsg(v)) cb(v);
        } catch {
          // not ours
        }
      };
      win.addEventListener('storage', handler);
    },
    close() {
      if (handler) win.removeEventListener('storage', handler);
      handler = null;
    },
  };
}

/** A short fingerprint of a stored text (FNV-1a 32 over the UTF-16 units + the length); null → 'none'. */
export function textFingerprint(text: string | null): string {
  if (text === null) return 'none';
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return `${text.length}:${h.toString(16)}`;
}

/**
 * The fingerprint of `key` in this document's localStorage (blocked storage → 'none'); several keys → their
 * fingerprints joined with '|' (the handover waits for the save AND the telemetry ring, main.ts).
 */
export function storedFingerprint(win: Window, key: string | readonly string[]): string {
  if (typeof key !== 'string') return key.map((k) => storedFingerprint(win, k)).join('|');
  try {
    return textFingerprint(win.localStorage.getItem(key));
  } catch {
    return textFingerprint(null);
  }
}

/**
 * Resolves true once this document's localStorage shows `token` under `key` (or every one of several keys:
 * `storedFingerprint`; checked now, on every `storage` event and every `pollMs`), false after `timeoutMs`
 * (the caller then goes on with what it has).
 */
export function waitForStored(
  win: Window,
  key: string | readonly string[],
  token: string,
  timeoutMs: number,
  pollMs = 10,
): Promise<boolean> {
  const keys: readonly string[] = typeof key === 'string' ? [key] : key;
  if (storedFingerprint(win, key) === token) return Promise.resolve(true);
  return new Promise<boolean>((resolve) => {
    let over = false;
    const finish = (ok: boolean): void => {
      if (over) return;
      over = true;
      win.removeEventListener('storage', onStorage);
      win.clearInterval(poll);
      win.clearTimeout(limit);
      resolve(ok);
    };
    const check = (): void => {
      if (storedFingerprint(win, key) === token) finish(true);
    };
    const onStorage = (ev: StorageEvent): void => {
      if (ev.key === null || keys.includes(ev.key)) check();
    };
    win.addEventListener('storage', onStorage);
    const poll = win.setInterval(check, pollMs);
    const limit = win.setTimeout(() => finish(storedFingerprint(win, key) === token), timeoutMs);
  });
}

/** max(wallMs, last + 1): a stored stamp at or past the wall clock (a clock set back) is outrun. */
export function nextStamp(last: string | null, wallMs: number): number {
  const n = last === null ? Number.NaN : Number(last);
  return Number.isFinite(n) && n >= wallMs ? n + 1 : wallMs;
}

/** The lock's storage marks on localStorage (every access guarded: blocked storage → no marks). */
export function storageMarks(win: Window): LockMarks {
  const get = (k: string): string | null => {
    try {
      return win.localStorage.getItem(k);
    } catch {
      return null;
    }
  };
  const set = (k: string, v: string): void => {
    try {
      win.localStorage.setItem(k, v);
    } catch {
      // quota / blocked: the protocol still works through messages
    }
  };
  return {
    stamp(wallMs) {
      const at = nextStamp(get(LOCK_AT_KEY), wallMs);
      set(LOCK_AT_KEY, String(at));
      return at;
    },
    owner: () => get(LOCK_OWNER_KEY),
    setOwner: (id) => set(LOCK_OWNER_KEY, id),
  };
}

/** In-memory marks shared by the instances of a test (one "device"). */
export function memoryMarks(): LockMarks & { data: Map<string, string> } {
  const data = new Map<string, string>();
  return {
    data,
    stamp(wallMs) {
      const at = nextStamp(data.get(LOCK_AT_KEY) ?? null, wallMs);
      data.set(LOCK_AT_KEY, String(at));
      return at;
    },
    owner: () => data.get(LOCK_OWNER_KEY) ?? null,
    setOwner: (id) => {
      data.set(LOCK_OWNER_KEY, id);
    },
  };
}

export function noTransport(): LockTransport {
  return { kind: 'none', post: () => undefined, listen: () => undefined, close: () => undefined };
}

function storageUsable(win: Window): boolean {
  try {
    const ls = win.localStorage;
    return ls !== null && ls !== undefined;
  } catch {
    return false;
  }
}

/** BroadcastChannel when the browser has it, else the storage-event fallback, else none. */
export function pickTransport(win: Window): LockTransport {
  const Ctor = (win as Window & { BroadcastChannel?: ChannelCtor }).BroadcastChannel;
  if (typeof Ctor === 'function') {
    try {
      return broadcastTransport(Ctor);
    } catch {
      // fall through
    }
  }
  return storageUsable(win) ? storageTransport(win) : noTransport();
}

/** A random instance id (crypto.getRandomValues also works outside secure contexts, unlike randomUUID). */
export function instanceId(cryptoObj: Pick<Crypto, 'getRandomValues'>): string {
  const a = new Uint32Array(4);
  cryptoObj.getRandomValues(a);
  return Array.from(a, (x) => x.toString(16).padStart(8, '0')).join('');
}
